import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import Box from '@mui/material/Box';
import { Grid, Paper, Alert } from '@mui/material';
import dayjs from 'dayjs'
import DataTable from '../../components/DataTable';
import DateRangeFilter from '../../components/DateRangeFilter';
import CustomPagination from '../../components/CustomPagination';
import MultiFilter from '../../components/MultiFilter';
import { getMessage } from '../../helpers';
import { appActions } from '../../redux/actions';
import CopyButton from '../../components/CopyButton';
import { tenantLogsActions } from '../../redux/actions/tenantlogs.actions';
import PageHeader from '../../components/common/PageHeader';
import StatusChip from '../../components/common/StatusChip';
import PageLoader from '../../components/common/PageLoader';


// set the default page size
const defaultPageSize = 30

// set the default last 7 days data
let defaultLast7DaysDate = new Date()
defaultLast7DaysDate.setDate(defaultLast7DaysDate.getDate() - 7)

// define cloumn definitions
const columns = [
  { field: 'id', headerName: 'ID' },
  { field: 'type', headerName: 'Event Type', flex: 1 },
  { field: 'actorUserId', headerName: 'Actor User ID', flex: 1 },
  { field: 'subjectId', headerName: 'Subject ID', flex: 1 },
  { field: 'subjectName', headerName: 'Subject Name', flex: 1 },
  {
    field: 'payload', headerName: 'Payload', flex: 1, valueFormatter: (params) => {
      // Check if the value is a JSON object
      if (typeof params.value === 'object') {
        return JSON.stringify(params.value);
      }
      return params.value;
    },
  },
  {
    field: 'status', headerName: 'Status', flex: 1, renderCell: (params) => (
      <StatusChip status={params?.value} />
    )
  },
  {
    field: 'ts', headerName: 'Timestamp', flex: 1, valueFormatter: (params) => {
      if (typeof params.value === 'number') {
        return dayjs(params.value).format('YYYY-MM-DD');
      }
      return params.value;
    },
  },
  {
    field: 'actions', headerName: 'Actions', renderCell: (params) => (
      <CopyButton row={params.row} />
    ),
  },
];


const TenantLogs = () => {

  const dispatch = useDispatch()
  const [, setLogicOperator] = useState('AND');
  const filteredRows = useSelector(state => state?.tenantLogsReducer?.tenantLogs)
  const tenantLogsLoading = useSelector(state => state?.tenantLogsReducer?.tenantLogsLoading)
  const tenantLogsErrorMessage = useSelector(
    (state) => state?.tenantLogsReducer?.tenantLogsErrorMessage
  )
  const [startDate, setStartDate] = useState(defaultLast7DaysDate);
  const [endDate, setEndDate] = useState(new Date());
  const [filters, setFilters] = useState([]);
  const [pageOffset, setPageOffset] = useState(0);

  useEffect(() => {
    applyDateFilters() // be default get the data for default start & end date
  }, [])

  const applyColumnFilters = (filters, logicOperator) => {
    let isDateFilterValid = handleDateFilterValidation()
    if (!isDateFilterValid) {
      return
    }

    let filterObject = {}
    if (filters && filters.length > 0) {
      filters.forEach(filter => {
        filterObject[filter["columnField"]] = filter["value"]
      })

      setFilters(filterObject)

      setPageOffset(0) // when column filter chnages, then fetch fresh data from starting

      dispatch(tenantLogsActions.getTenantLogs({
        startTs: toUnixTs(startDate),
        endTs: toUnixTs(endDate),
        index: 0,
        count: defaultPageSize,
        ...filterObject
      }))
    }
    else {
      setFilters({}) // remove the filters

      setPageOffset(0) // when column filter chnages, then fetch fresh data from starting

      dispatch(tenantLogsActions.getTenantLogs({
        startTs: toUnixTs(startDate),
        endTs: toUnixTs(endDate),
        index: 0,
        count: defaultPageSize
      }))
    }
  };

  const applyDateFilters = () => {
    let isDateFilterValid = handleDateFilterValidation()
    if (!isDateFilterValid) {
      return
    }

    setPageOffset(0) // when date filter chnages, then fetch fresh data from starting

    dispatch(tenantLogsActions.getTenantLogs({
      startTs: toUnixTs(startDate),
      endTs: toUnixTs(endDate),
      index: 0,
      count: defaultPageSize,
      ...filters
    }))
  }

  const handlePageChange = (newPage) => {
    setPageOffset(newPage);

    dispatch(tenantLogsActions.getTenantLogs({
      startTs: toUnixTs(startDate),
      endTs: toUnixTs(endDate),
      index: newPage,
      count: defaultPageSize,
      ...filters
    }))
  };

  const handleDateFilterValidation = () => {
    if (startDate == null || endDate == null) {
      return true
    }
    if (new Date(startDate).getTime() > new Date(endDate).getTime()) {
      getMessage(`tenant-logs-start-date-greater-than-end-date`).then((msg) => {
        dispatch(appActions.apiErrorMessage(msg));
      })
      return false
    }
    return true
  }

  const toUnixTs = (value) => Math.floor(new Date(value).getTime() / 1000)


  return (
    <Box sx={{ width: '100%' }}>
      <PageHeader
        title="Tenant Logs"
        subtitle="Monitor tenant-level audit activity and system events."
      />
      <Paper sx={{ p: 2.5 }}>
      <Grid container spacing={2} alignItems="center" sx={{ mb: 0.5 }}>
        <Grid item xs={12} md={5}>
          <MultiFilter
            columns={columns}
            onFiltersChange={(filters, logicOperator) => {
              setFilters(filters);
              setLogicOperator(logicOperator);
              applyColumnFilters(filters, logicOperator);
            }}>
          </MultiFilter>
        </Grid>
        <Grid item xs={12} md={7}>
          <DateRangeFilter
            startDate={startDate}
            endDate={endDate}
            onDateChange={(field, value) => {
              if (field === 'startDate') {
                setStartDate(value);
              } else if (field === 'endDate') {
                setEndDate(value);
              }
            }}
            applyFilters={applyDateFilters}
          >
          </DateRangeFilter>
        </Grid>
      </Grid>
      {tenantLogsErrorMessage ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {tenantLogsErrorMessage}
        </Alert>
      ) : null}
      {tenantLogsLoading ? (
        <PageLoader text="Loading tenant logs..." />
      ) : (
        <DataTable
          rows={filteredRows && filteredRows.map((row, index) => ({
            ...row,
            id: index + 1,
          }))}
          loading={tenantLogsLoading}
          columns={columns}
          pageSize={defaultPageSize}
          showPagination={false}
          columnVisibilityModel={{
            id: false
          }}
          styling={{
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: '#f8fafc',
            },
          }}
        >
        </DataTable>
      )}
      {!tenantLogsLoading ? (
        <div style={{ justifyContent: 'flex-end', display: 'flex', marginTop: '10px' }}>
          <CustomPagination
            page={pageOffset}
            filteredRows={filteredRows.length}
            maxRowsPerPage={defaultPageSize}
            onPageChange={handlePageChange}
            sx={{
              borderTop: "none",
              backgroundColor: "transparent",
            }}
          />
        </div>
      ) : null}
      </Paper>
    </Box>
  );
}

export default TenantLogs;